import { getPrismicLocale, type Locales } from "@/lib/i18n";
import type { ProductDocument } from "@/prismicio-types";

export type MedusaProductDocumentSource = {
  id: string;
  status?: string | null;
  title: string;
  description?: string | null;
  handle?: string | null;
  created_at?: string | Date | null;
  updated_at?: string | Date | null;
  metadata?: Record<string, unknown> | null;
};

export type StorefrontPrismicProduct = ProductDocument & {
  translation?: ProductDocument | null;
};

const LOCALIZED_METADATA_KEYS = {
  fr: { title: null, description: null },
  nl: { title: "title_nl", description: "description_nl" },
  en: { title: "title_en", description: "description_en" },
} as const satisfies Record<Locales, { title: string | null; description: string | null }>;

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

export const getLinkedMedusaProductId = (
  product: StorefrontPrismicProduct | null,
): string | null =>
  nonEmptyString(product?.translation?.data.medusaId) ||
  nonEmptyString(product?.data.medusaId) ||
  null;

export const getMedusaLookupHandle = (
  product: StorefrontPrismicProduct | null,
  routeHandle: string,
): string => nonEmptyString(product?.translation?.uid) || routeHandle;

const isEditorialCollection = (
  product: StorefrontPrismicProduct | null,
): boolean =>
  product?.data.productCollection === true ||
  product?.translation?.data.productCollection === true;

const toRichText = (text: string): ProductDocument["data"]["description"] =>
  text
    ? [
        {
          type: "paragraph",
          text,
          spans: [],
        },
      ]
    : [];

const toIsoString = (value: string | Date | null | undefined): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const createMedusaProductDocument = (
  product: MedusaProductDocumentSource,
  locale: Locales,
): ProductDocument => {
  const handle = nonEmptyString(product.handle);
  if (!handle) throw new Error(`Missing product handle for ${product.id}`);

  const metadata = product.metadata || {};
  const localizedKeys = LOCALIZED_METADATA_KEYS[locale];
  const title =
    (localizedKeys.title && nonEmptyString(metadata[localizedKeys.title])) ||
    nonEmptyString(product.title);
  if (!title) throw new Error(`Missing product title for ${product.id}`);

  const description =
    (localizedKeys.description && nonEmptyString(metadata[localizedKeys.description])) ||
    nonEmptyString(product.description) ||
    "";
  const createdAt = toIsoString(product.created_at) || "1970-01-01T00:00:00.000Z";
  const updatedAt = toIsoString(product.updated_at) || createdAt;

  return {
    id: `medusa_${locale}_${product.id}`,
    uid: handle,
    url: null,
    type: "product",
    href: "",
    tags: [],
    first_publication_date: createdAt,
    last_publication_date: updatedAt,
    slugs: [handle],
    linked_documents: [],
    lang: getPrismicLocale(locale),
    alternate_languages: [],
    data: {
      title,
      description: toRichText(description),
      informations_completes: toRichText(description),
      images: [],
      display: 0,
      consigne: [],
      questions: [],
      image_full_size_card: false,
      medusaId: product.id,
      slices: [],
      meta_title: title,
      meta_description: description,
      meta_image: {},
      og_title: title,
      og_description: description,
      og_image: {},
      twitter_title: title,
      twitter_description: description,
      twitter_image: {},
      index: true,
      follow: true,
      url_canonical: null,
      categories: [],
      productCollection: false,
      featuredImage: {},
      products: [],
      onlyDisplayFirstImage: false,
      relatedProducts: [],
      linkedInspirations: [],
      linkedTables: [],
    },
  };
};

export const selectStorefrontProductDocument = (input: {
  prismicProduct: StorefrontPrismicProduct | null;
  medusaProduct: MedusaProductDocumentSource | null;
  locale: Locales;
}): ProductDocument | null => {
  if (isEditorialCollection(input.prismicProduct)) {
    return input.prismicProduct;
  }

  if (!input.medusaProduct || input.medusaProduct.status !== "published") {
    return null;
  }

  const prismicMedusaId = getLinkedMedusaProductId(input.prismicProduct);
  if (
    input.prismicProduct?.type === "product" &&
    prismicMedusaId === input.medusaProduct.id
  ) {
    return input.prismicProduct;
  }

  return createMedusaProductDocument(input.medusaProduct, input.locale);
};
